import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, TABLES } from '../api/db';
import { Brain, BookOpen, User, Calendar, Loader, AlertCircle, ExternalLink } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AIAnalysis, Task } from '../types';
import AIAnalysisDisplay from '../components/AIAnalysisDisplay';
import { normalizeAIAnalysisRow, normalizeTaskRow } from '../utils/taskRecords';

interface TaskWithAnalysis extends Task {
  aiAnalysis?: AIAnalysis;
  hasAiAnalysis?: boolean;
}

const ImplementationGuides = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState<TaskWithAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<TaskWithAnalysis | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [filter, setFilter] = useState<'all' | 'my-tasks' | 'public'>('all');

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const loadTasks = async () => {
      setLoading(true);
      try {
        let query = supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').order('created_at', { ascending: false });
        if (filter === 'my-tasks') {
          query = query.eq('user_id', currentUser.uid);
        } else if (filter === 'public') {
          query = query.eq('data_sensitivity', 'public').neq('user_id', currentUser.uid);
        }
        const { data } = await query;
        const baseTasks = (data ?? []).map((r) => normalizeTaskRow(r as Record<string, unknown>) as TaskWithAnalysis);
        const taskIds = baseTasks.map((task) => task.id);

        let tasksWithAnalyses = baseTasks.map((task) => ({ ...task, hasAiAnalysis: false }));
        if (taskIds.length > 0) {
          const { data: analysisRows } = await supabase
            .from(TABLES.AI_ANALYSES)
            .select('*')
            .in('task_id', taskIds)
            .order('created_at', { ascending: false });

          const latestByTaskId = new Map<string, AIAnalysis>();
          (analysisRows ?? []).forEach((row) => {
            const analysis = normalizeAIAnalysisRow(row as Record<string, unknown>);
            if (!latestByTaskId.has(analysis.taskId)) {
              latestByTaskId.set(analysis.taskId, analysis);
            }
          });

          tasksWithAnalyses = baseTasks.map((task) => {
            const analysis = latestByTaskId.get(task.id);
            return analysis
              ? { ...task, aiAnalysis: analysis, hasAiAnalysis: true }
              : { ...task, hasAiAnalysis: false };
          });
        }

        setTasks(tasksWithAnalyses);

        const selectedTaskId = searchParams.get('task');
        const selectedAnalysisId = searchParams.get('analysisId');
        if (selectedTaskId) {
          const matchedTask = tasksWithAnalyses.find((task) => task.id === selectedTaskId) || null;
          setSelectedTask(matchedTask);
        } else if (selectedAnalysisId) {
          const matchedTask = tasksWithAnalyses.find((task) => task.aiAnalysis?.id === selectedAnalysisId) || null;
          setSelectedTask(matchedTask);
        }
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadTasks();
    return () => {};
  }, [currentUser, filter, searchParams]);

  const loadAnalysis = async (task: TaskWithAnalysis) => {
    if (task.aiAnalysis) {
      setSelectedTask(task);
      return;
    }

    if (!task.hasAiAnalysis) {
      setSelectedTask(task);
      return;
    }

    setLoadingAnalysis(true);
    setSelectedTask(task);
    setLoadingAnalysis(false);
  };

  const formatDate = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-amber-500/20 text-amber-300 border-amber-500/50';
      case 'high': return 'bg-amber-500/20 text-amber-200 border-amber-500/50';
      case 'medium': return 'bg-tech-cyan/20 text-tech-cyan border-tech-cyan/50';
      case 'low': return 'bg-tech-accent/20 text-tech-accent border-tech-accent/50';
      default: return 'bg-tech-gray-light text-tech-text-muted border-tech-border';
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-tech-cyan mx-auto mb-4" aria-hidden />
          <p className="text-tech-text-muted text-lg">Please log in to view implementation guides.</p>
          <Link to="/login" className="inline-block mt-4 px-4 py-2 bg-tech-cyan/20 border border-tech-cyan rounded-lg text-tech-cyan hover:bg-tech-cyan/30 transition-colors">
            Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text px-4 py-12 bg-tech-mesh">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BookOpen className="w-10 h-10 text-tech-cyan" aria-hidden />
            <div>
              <h1 className="text-3xl font-bold text-tech-text">Implementation Guides</h1>
              <p className="text-tech-text-muted">AI-generated guides for task execution</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/task-hatch')}
            className="px-4 py-2 rounded-lg font-semibold text-tech-dark hover:opacity-90 transition-opacity flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
            style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
          >
            <Brain className="w-5 h-5" aria-hidden />
            New task
          </button>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {(['all', 'my-tasks', 'public'] as const).map((filterType) => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === filterType
                  ? 'bg-tech-cyan text-tech-dark'
                  : 'tech-card text-tech-text-muted hover:text-tech-text'
              }`}
            >
              {filterType === 'my-tasks' ? 'My Tasks' : filterType === 'public' ? 'Public Tasks' : 'All Tasks'}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Tasks List */}
          <div className="lg:col-span-1">
            {loading ? (
              <div className="text-center py-12">
                <Loader className="w-12 h-12 text-tech-cyan mx-auto animate-spin" aria-hidden />
                <p className="text-tech-text-muted mt-4">Loading tasks...</p>
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-12 tech-card rounded-xl">
                <BookOpen className="w-16 h-16 text-tech-cyan/50 mx-auto mb-4" aria-hidden />
                <p className="text-tech-text text-lg">No tasks found</p>
                <p className="text-tech-text-muted text-sm mt-2">
                  {filter === 'all'
                    ? 'Create your first task to get started'
                    : `No ${filter === 'my-tasks' ? 'your' : 'public'} tasks at the moment`}
                </p>
                <button
                  onClick={() => navigate('/task-hatch')}
                  className="mt-4 px-4 py-2 rounded-lg font-semibold text-tech-dark focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
                  style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
                >
                  Create your first task
                </button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => loadAnalysis(task)}
                    className={`tech-card rounded-xl p-4 cursor-pointer transition ${
                      selectedTask?.id === task.id
                        ? 'border-tech-cyan/50 bg-tech-cyan/10'
                        : 'hover:border-tech-cyan/20'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-tech-text line-clamp-2">{task.title}</h3>
                      {task.userId === currentUser.uid && (
                        <span className="px-2 py-1 text-xs bg-tech-cyan/20 text-tech-cyan rounded">Mine</span>
                      )}
                    </div>
                    <p className="text-tech-text-muted text-sm mb-3 line-clamp-2">{task.summary}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-2 py-1 rounded-full border ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      {task.category && (
                        <span className="px-2 py-1 rounded-full bg-tech-cyan/20 text-tech-cyan border border-tech-cyan/50">
                          {task.category}
                        </span>
                      )}
                      {task.hasAiAnalysis && (
                        <span className="px-2 py-1 rounded-full bg-tech-accent/20 text-tech-accent border border-tech-accent/50 flex items-center gap-1">
                          <Brain className="w-3 h-3" />
                          Guide
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-xs text-tech-text-muted">
                      <User className="w-3 h-3" />
                      <span>{task.userName}</span>
                      <Calendar className="w-3 h-3 ml-2" />
                      <span>{formatDate(task.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Implementation Guide Display */}
          <div className="lg:col-span-2">
            {loadingAnalysis ? (
              <div className="text-center py-12 tech-card rounded-xl">
                <Loader className="w-12 h-12 text-tech-cyan mx-auto animate-spin" aria-hidden />
                <p className="text-tech-text-muted mt-4">Loading implementation guide...</p>
              </div>
            ) : selectedTask ? (
              <div className="tech-card rounded-xl p-6 md:p-8 space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-tech-text mb-2">{selectedTask.title}</h2>
                    <p className="text-tech-text-muted mb-4">{selectedTask.summary}</p>
                    <div className="flex items-center gap-4 text-sm text-tech-text-muted">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {selectedTask.userName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedTask.createdAt)}
                      </span>
                    </div>
                  </div>
                  {selectedTask.userId === currentUser.uid && (
                    <button
                      onClick={() => navigate(`/task-dashboard?edit=${selectedTask.id}`)}
                      className="px-4 py-2 tech-card rounded-lg text-tech-cyan border border-tech-cyan/50 hover:border-tech-cyan transition flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Edit Task
                    </button>
                  )}
                </div>

                {selectedTask.aiAnalysis ? (
                  <div className="mt-6">
                    <div className="flex items-center gap-3 mb-6">
                      <Brain className="w-8 h-8 text-tech-cyan" aria-hidden />
                      <h3 className="text-xl font-bold text-tech-text">
                        Implementation Guide
                      </h3>
                    </div>
                    <AIAnalysisDisplay analysis={selectedTask.aiAnalysis} />
                  </div>
                ) : selectedTask.hasAiAnalysis ? (
                  <div className="text-center py-12 tech-card rounded-xl">
                    <Loader className="w-12 h-12 text-tech-cyan mx-auto animate-spin mb-4" aria-hidden />
                    <p className="text-tech-text-muted">Implementation guide is being generated...</p>
                    <p className="text-tech-text-muted text-sm mt-2">Please check back in a few moments</p>
                  </div>
                ) : (
                  <div className="text-center py-12 tech-card rounded-xl">
                    <AlertCircle className="w-12 h-12 text-tech-cyan/50 mx-auto mb-4" aria-hidden />
                    <p className="text-tech-text-muted">No implementation guide available yet</p>
                    <p className="text-tech-text-muted text-sm mt-2">AI analysis may still be processing</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 tech-card rounded-xl">
                <BookOpen className="w-16 h-16 text-tech-cyan/50 mx-auto mb-4" aria-hidden />
                <p className="text-tech-text-muted text-lg">Select a task to view its implementation guide</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImplementationGuides;
